import { createFileRoute } from "@tanstack/react-router";
import { useTenant } from "../lib/tenant-context";

export const Route = createFileRoute("/warehouse/overview")({
  component: WarehouseOverview,
});

interface WarehouseMetrics {
  totalPackages: number;
  pendingProcessing: number;
  inTransit: number;
  completedThisWeek: number;
  itemsRecovered: number;
  itemsLabel: string;
  averageProcessingTime: string;
  topLocations: { name: string; packages: number }[];
  stageBreakdown: { stage: string; count: number; color: string }[];
}

// Nespresso metrics
const NESPRESSO_METRICS: WarehouseMetrics = {
  totalPackages: 156,
  pendingProcessing: 23,
  inTransit: 45,
  completedThisWeek: 88,
  itemsRecovered: 4250,
  itemsLabel: "Total capsules recycled",
  averageProcessingTime: "2.4 hours",
  topLocations: [
    { name: "Nespresso Boutique Champs-Élysées", packages: 42 },
    { name: "Galeries Lafayette Haussmann", packages: 38 },
    { name: "Le Bon Marché", packages: 31 },
    { name: "Printemps Haussmann", packages: 27 },
    { name: "Nespresso Boutique Opéra", packages: 18 },
  ],
  stageBreakdown: [
    { stage: "received", count: 23, color: "#F59E0B" },
    { stage: "in_transit", count: 45, color: "#3B82F6" },
    { stage: "processing", count: 12, color: "#8B5CF6" },
    { stage: "completed", count: 76, color: "#10B981" },
  ],
};

// Adidas metrics
const ADIDAS_METRICS: WarehouseMetrics = {
  totalPackages: 312,
  pendingProcessing: 47,
  inTransit: 82,
  completedThisWeek: 134,
  itemsRecovered: 1840,
  itemsLabel: "Total items assessed by VLM",
  averageProcessingTime: "1.8 hours",
  topLocations: [
    { name: "adidas Flagship Oxford Street, London", packages: 68 },
    { name: "adidas Store Brooklyn, New York", packages: 54 },
    { name: "adidas Flagship Champs-Élysées, Paris", packages: 49 },
    { name: "adidas Brand Center Tokyo", packages: 41 },
    { name: "adidas Flagship Herzogenaurach", packages: 36 },
  ],
  stageBreakdown: [
    { stage: "received", count: 47, color: "#F59E0B" },
    { stage: "in_transit", count: 82, color: "#3B82F6" },
    { stage: "processing", count: 28, color: "#8B5CF6" },
    { stage: "completed", count: 155, color: "#10B981" },
  ],
};

const TENANT_METRICS: Record<string, WarehouseMetrics> = {
  nespresso: NESPRESSO_METRICS,
  adidas: ADIDAS_METRICS,
};

function WarehouseOverview() {
  const { tenantId } = useTenant();
  const metrics = TENANT_METRICS[tenantId || "nespresso"] || NESPRESSO_METRICS;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
          Warehouse Overview
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Summary of takeback package processing metrics
        </p>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total Packages"
          value={metrics.totalPackages.toString()}
          subtitle="All time"
          icon={<PackageIcon />}
        />
        <MetricCard
          title="Pending Processing"
          value={metrics.pendingProcessing.toString()}
          subtitle="Awaiting warehouse"
          icon={<ClockIcon />}
          highlight
        />
        <MetricCard
          title="Completed This Week"
          value={metrics.completedThisWeek.toString()}
          subtitle="Last 7 days"
          icon={<CheckIcon />}
        />
        <MetricCard
          title="Items Recovered"
          value={metrics.itemsRecovered.toLocaleString()}
          subtitle={metrics.itemsLabel}
          icon={<RecycleIcon />}
        />
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Stage Breakdown */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Processing Stages
          </h2>
          <div className="space-y-4">
            {metrics.stageBreakdown.map((item) => (
              <div key={item.stage} className="flex items-center gap-3">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: item.color }}
                />
                <div className="flex-1">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300 capitalize">
                      {item.stage.replace("_", " ")}
                    </span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {item.count}
                    </span>
                  </div>
                  <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-2">
                    <div
                      className="h-2 rounded-full transition-all"
                      style={{
                        width: `${(item.count / metrics.totalPackages) * 100}%`,
                        backgroundColor: item.color,
                      }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top Locations */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Top Drop-off Locations
          </h2>
          <div className="space-y-3">
            {metrics.topLocations.map((location, index) => (
              <div
                key={location.name}
                className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700 last:border-0"
              >
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-gray-400 w-5">
                    {index + 1}.
                  </span>
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {location.name}
                  </span>
                </div>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {location.packages}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Performance Stats */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
        <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          Performance
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Avg. Processing Time
            </p>
            <p className="text-2xl font-semibold text-gray-900 dark:text-white mt-1">
              {metrics.averageProcessingTime}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              In Transit
            </p>
            <p className="text-2xl font-semibold text-gray-900 dark:text-white mt-1">
              {metrics.inTransit} packages
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Weekly Completion Rate
            </p>
            <p className="text-2xl font-semibold text-gray-900 dark:text-white mt-1">
              {Math.round(
                (metrics.completedThisWeek / metrics.totalPackages) * 100,
              )}
              %
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function MetricCard({
  title,
  value,
  subtitle,
  icon,
  highlight = false,
}: {
  title: string;
  value: string;
  subtitle: string;
  icon: React.ReactNode;
  highlight?: boolean;
}) {
  return (
    <div
      className={`rounded-lg border p-5 ${
        highlight
          ? "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800"
          : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
      }`}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500 dark:text-gray-400">{title}</p>
          <p className="text-2xl font-semibold text-gray-900 dark:text-white mt-1">
            {value}
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            {subtitle}
          </p>
        </div>
        <div className="text-gray-400 dark:text-gray-500">{icon}</div>
      </div>
    </div>
  );
}

// Icons
function PackageIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path
        d="M16.5 9.4L7.5 4.21"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M21 16V8C20.9996 7.6488 20.9071 7.30393 20.7315 7.00106C20.5559 6.69818 20.3034 6.44854 20 6.276L13 2.276C12.696 2.10315 12.3511 2.0127 12 2.0127C11.6489 2.0127 11.304 2.10315 11 2.276L4 6.276C3.69663 6.44854 3.44414 6.69818 3.26849 7.00106C3.09285 7.30393 3.00036 7.6488 3 8V16C3.00036 16.3512 3.09285 16.6961 3.26849 16.9989C3.44414 17.3018 3.69663 17.5515 4 17.724L11 21.724C11.304 21.8968 11.6489 21.9873 12 21.9873C12.3511 21.9873 12.696 21.8968 13 21.724L20 17.724C20.3034 17.5515 20.5559 17.3018 20.7315 16.9989C20.9071 16.6961 20.9996 16.3512 21 16Z"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M3.27 6.96L12 12.01L20.73 6.96"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M12 22.08V12" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6V12L16 14" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path
        d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.709 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18457 2.99721 7.13633 4.39828 5.49707C5.79935 3.85782 7.69279 2.71538 9.79619 2.24015C11.8996 1.76491 14.1003 1.98234 16.07 2.86"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M22 4L12 14.01L9 11.01"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function RecycleIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path
        d="M7 19H4.815C4.40503 19.0004 4.00761 18.8669 3.68479 18.6202C3.36197 18.3736 3.13215 18.0275 3.029 17.635L2.214 14.391C2.14316 14.1088 2.14133 13.8136 2.20866 13.5306C2.27598 13.2476 2.41043 12.9852 2.60067 12.7653C2.79091 12.5455 3.03132 12.3746 3.3016 12.2675C3.57188 12.1604 3.86383 12.1203 4.153 12.15L7 12.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M7 19L10 15.5" strokeLinecap="round" strokeLinejoin="round" />
      <path
        d="M7 19C7 19 5 16.5 5 14C5 11.5 7 9 7 9"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M17 5H19.185C19.595 4.99959 19.9924 5.13312 20.3152 5.37978C20.638 5.62644 20.8679 5.97246 20.971 6.365L21.786 9.609C21.8568 9.89122 21.8587 10.1864 21.7913 10.4694C21.724 10.7524 21.5896 11.0148 21.3993 11.2347C21.2091 11.4545 20.9687 11.6254 20.6984 11.7325C20.4281 11.8396 20.1362 11.8797 19.847 11.85L17 11.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M17 5L14 8.5" strokeLinecap="round" strokeLinejoin="round" />
      <path
        d="M17 5C17 5 19 7.5 19 10C19 12.5 17 15 17 15"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 22C12.5304 22 13.0391 21.7893 13.4142 21.4142C13.7893 21.0391 14 20.5304 14 20V17.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M12 22L8.5 19" strokeLinecap="round" strokeLinejoin="round" />
      <path
        d="M12 2C11.4696 2 10.9609 2.21071 10.5858 2.58579C10.2107 2.96086 10 3.46957 10 4V6.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M12 2L15.5 5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
